import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import "./layersStyles.css";
import { Input, Tree, TreeDataNode } from 'antd';
import Search from "../../../assets/icons/Search.svg";
import { convertToAntdTree, filterAndHighlightTree, transformNodesToQA } from '../../../utilities/Nodes/convertToLayers';
import { useTranslation } from 'react-i18next';
import { useFlowDataContext } from '../../../context/workbench/FlowDataContext';
import { useLayerContext } from '../../../context/workbench/LayerContext';

const Layers = () => {
    const { t } = useTranslation("common");
    const flowDataContext = useFlowDataContext();
    const layerContext = useLayerContext();

    const [layersOpen] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
    const [searchExpandedKeys, setSearchExpandedKeys] = useState<React.Key[]>([]);
    const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]); // Add state for selected keys
    const [treeData, setTreeData] = useState<TreeDataNode[]>([]);

    // Convert and filter tree data based on search term
    useEffect(() => {
        const searchKeys: React.Key[] = [];
        const dataConversion = convertToAntdTree(transformNodesToQA(flowDataContext?.nodes, flowDataContext?.edges));
        const filteredTreeData = searchTerm
            ? filterAndHighlightTree(dataConversion, searchTerm, searchKeys)
            : dataConversion;

        setTreeData(filteredTreeData);

        // Only update expanded keys for search results
        if (searchTerm) {
            setSearchExpandedKeys(searchKeys);
        } else {
            setSearchExpandedKeys([]);
        }
    }, [flowDataContext?.edges, flowDataContext?.nodes, searchTerm]);

    // Combine manual expansion with search expansion
    const getAllExpandedKeys = useMemo(() => {
        if (searchTerm) {
            return Array.from(new Set([...expandedKeys, ...searchExpandedKeys]));
        }
        return expandedKeys;
    }, [expandedKeys, searchExpandedKeys, searchTerm]);


    // Handle tree selection
    const handleSelect = useCallback((newSelectedKeys: React.Key[], info: any) => {
        // Always set to either the newly selected key or empty array
        setSelectedKeys(newSelectedKeys.length > 0 ? [newSelectedKeys[newSelectedKeys.length - 1]] : []);

        const nodeId = info.node.metadata?.nodeId ||
            info.node.metadata?.questionNodeId ||
            info.node.metadata?.uploadNodeId;

        if (nodeId) {
            layerContext.setLayerSelectedNodeId(nodeId);
        }
    }, [layerContext]);

    return (
        <div className={`layers-wrapper ${layersOpen ? 'open' : 'close'}`}>
            <div className="d-flex justify-content-between align-items-center mt-2">
                <p className="mb-0 title">{t('layers')}</p>
                {/* <Button
                    className={`layers-handle p-0 bg-transparent ${layersOpen ? 'open' : 'close'}`}
                    onClick={handleLayersOpenClose}
                >
                    <img width={28} src={layersHide} alt="layers toggle" />
                </Button> */}
            </div>
            <Input
                size="large"
                type="search"
                placeholder={t('layersSearchPlaceholder')}
                prefix={<img src={Search} alt="search" className='search-icon'/>}
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="tree-wrapper">
                {treeData?.map((node: any) => (
                    <div key={node.key} className='customTree' tabIndex={0}>
                        <Tree
                            treeData={[node]}
                            expandedKeys={getAllExpandedKeys}
                            selectedKeys={selectedKeys}
                            onExpand={setExpandedKeys}
                            onSelect={handleSelect}
                            className="layer-structure"
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default memo(Layers);